/**
 * Id generation (W1-01 core-time-math):
 *  - ProjectId: 12 characters of lowercase Crockford base32 from crypto.getRandomValues (60 random bits; no
 *    i, l, o, u, so ids read back unambiguously and are safe in URLs and file names);
 *  - RPE id (`META.id`, info.txt `Path`): a 10-digit numeric string, generated at project creation;
 *  - numeric document ids come from one per-document counter (`ChartDoc.idSeq`, core/model); `createIdCounter`
 *    is the small allocator for code that hands out several ids at once.
 *
 * Every generator takes an optional byte source so tests are deterministic.
 */

import type { ProjectId } from '../model/types';

/** Fills the buffer with random bytes (default: crypto.getRandomValues). */
export type RandomBytes = (buf: Uint8Array<ArrayBuffer>) => void;

/** Lowercase Crockford base32 alphabet. */
export const BASE32_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';

export const PROJECT_ID_LENGTH = 12;
export const RPE_ID_LENGTH = 10;

const PROJECT_ID_RE = /^[0-9a-hjkmnp-tv-z]{12}$/;
const NUMERIC_ID_RE = /^[0-9]+$/;

const cryptoBytes: RandomBytes = (buf) => {
  globalThis.crypto.getRandomValues(buf);
};

/** Random lowercase Crockford base32 string (each character is uniform: 256 is a multiple of 32). */
export function randomBase32(length: number, random: RandomBytes = cryptoBytes): string {
  if (!Number.isSafeInteger(length) || length < 0) throw new RangeError('ids: length must be a non-negative integer');
  const bytes = new Uint8Array(length);
  random(bytes);
  let out = '';
  for (const byte of bytes) out += BASE32_ALPHABET.charAt(byte & 31);
  return out;
}

/** A new ProjectId (12 base32 characters). */
export function newProjectId(random: RandomBytes = cryptoBytes): ProjectId {
  return randomBase32(PROJECT_ID_LENGTH, random) as ProjectId;
}

export function isProjectId(s: string): s is ProjectId {
  return PROJECT_ID_RE.test(s);
}

/** Validates a string as a ProjectId (e.g. a route parameter); null when it is not one. */
export function parseProjectId(s: string): ProjectId | null {
  return isProjectId(s) ? s : null;
}

/**
 * Uniform random decimal digits by rejection sampling (bytes >= 250 are redrawn, so 0..9 stay equally likely).
 */
function randomDigits(count: number, random: RandomBytes): string {
  let out = '';
  while (out.length < count) {
    const buf = new Uint8Array(count - out.length);
    random(buf);
    for (const byte of buf) if (byte < 250) out += String(byte % 10);
  }
  return out;
}

/**
 * A new RPE id: 10 decimal digits, the first one non-zero (RPE writes numeric ids). `taken` ids are avoided,
 * e.g. the ids of the other difficulties of the same song.
 */
export function newRpeId(random: RandomBytes = cryptoBytes, taken?: ReadonlySet<string>): string {
  for (;;) {
    const first = randomDigits(1, random);
    if (first === '0') continue;
    const id = first + randomDigits(RPE_ID_LENGTH - 1, random);
    if (taken === undefined || !taken.has(id)) return id;
  }
}

/** True for a non-empty string of ASCII digits (the RPE id shape). */
export function isNumericId(s: string): boolean {
  return NUMERIC_ID_RE.test(s);
}

/** Sequential allocator over a numeric id counter (`ChartDoc.idSeq` semantics: never reused). */
export interface IdCounter {
  /** Hands out the current value and advances. */
  next(): number;
  /** The value the next call to `next` returns (the new `idSeq` to store). */
  readonly peek: number;
}

export function createIdCounter(start: number): IdCounter {
  if (!Number.isSafeInteger(start) || start < 0)
    throw new RangeError('ids: counter start must be a non-negative integer');
  let value = start;
  return {
    next() {
      const id = value;
      value += 1;
      return id;
    },
    get peek() {
      return value;
    },
  };
}

/**
 * A name that does not collide with `taken`: `base` itself when free, else `base 2`, `base 3`, ... (used for
 * duplicated lines, layers, templates and project copies).
 */
export function uniqueName(base: string, taken: ReadonlySet<string>): string {
  if (!taken.has(base)) return base;
  for (let i = 2; ; i++) {
    const candidate = `${base} ${String(i)}`;
    if (!taken.has(candidate)) return candidate;
  }
}
